// Public engineer PWA install guide at /engineer/install (no auth). A one-page, phone-first
// walkthrough to add the app to a home screen, with a QR code of the engineer PWA URL generated
// client-side (qrcode). Static: no backend needed.
//
// Session AFG: the steps are PLATFORM-DETECTED from the user-agent (Android shows the Chrome steps,
// iPhone/iPad shows the Safari steps, one at a time so there is no confusion), each step a numbered
// circle. A desktop / unknown device shows both, Android first.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { ELE_LOGO_DATA_URI } from "../../lib/eleLogo";

// The engineer PWA URL the page shows and the QR encodes (Session AFG: /engineer, the engineer
// manifest's start_url, NOT /engineer/login). Opening /engineer serves the engineer-scoped manifest
// so "Add to Home Screen" installs the "ELE Engineer" app, and an unauthenticated visitor is routed
// on to the engineer sign-in by the engineer guard. Session ADU: resolved from
// window.location.origin, NOT a hardcoded onrender host. This is how the rest of the client builds
// first-party URLs (RfidTagButton, QuotesPage) -- the SPA has no VITE_ env mechanism by design
// (vite.config.ts). It self-follows wherever the app is served, so when the Director moves to a
// custom domain the QR and the address update with no code change; a hardcoded host would keep
// sending engineers to the old domain. Guarded for a non-browser (test) context.
function engineerAppUrl(): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/engineer`;
}

export type InstallPlatform = "ios" | "android" | "other";

// Pure platform detection from a user-agent string (exported for tests). An iPhone / iPad / iPod is
// iOS (Safari steps); an Android device is Android (Chrome steps); everything else shows both. Kept
// deliberately simple: it only decides which install walkthrough to show, so an iPadOS-13+ device
// that reports as a Mac harmlessly falls into "other" and sees both.
export function detectInstallPlatform(ua: string): InstallPlatform {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

interface Guide {
  heading: string;
  steps: string[];
  note: string;
}

const ANDROID: Guide = {
  heading: "Install on Android",
  steps: ["Tap the menu icon (three dots, top right of Chrome).", 'Tap "Add to Home Screen".', 'Tap "Install".'],
  note: "Must be using Chrome. If you are in another browser, copy the link and open it in Chrome first.",
};

const IOS: Guide = {
  heading: "Install on iPhone",
  steps: [
    "Make sure you are using Safari, not Chrome or another browser.",
    "Tap the Share button at the bottom of the screen (a box with an arrow).",
    'Scroll down and tap "Add to Home Screen".',
    'Tap "Add" in the top right.',
  ],
  note: "Must be using Safari. Chrome on iPhone cannot install apps to your home screen.",
};

// One platform's numbered walkthrough. The step number is a filled purple circle with a white
// numeral (20px); the step text is Montserrat 14px white; the heading is Rajdhani 16px ELE blue.
function InstallSteps({ guide }: { guide: Guide }) {
  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-4 font-heading text-base text-[var(--ele-loler-ink)]">{guide.heading}</h2>
      <ol className="space-y-3">
        {guide.steps.map((step, i) => (
          <li key={step} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold leading-none text-white"
              style={{ backgroundColor: "#7d21c7" }}
            >
              {i + 1}
            </span>
            <span className="font-body text-sm text-ele-strong">{step}</span>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-xs text-ele-body">{guide.note}</p>
    </section>
  );
}

export function EngineerInstall() {
  const [qr, setQr] = useState<string | null>(null);
  const appUrl = engineerAppUrl();
  const platform = detectInstallPlatform(typeof navigator !== "undefined" ? navigator.userAgent : "");

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(appUrl, { width: 232, margin: 1 }).then(
      (dataUrl) => {
        if (live) setQr(dataUrl);
      },
      () => {
        /* The QR is a convenience; the typed address below still works without it. */
      },
    );
    return () => {
      live = false;
    };
  }, [appUrl]);

  return (
    <div className="engineer-ui min-h-dvh bg-ele-base text-ele-body">
      <header className="flex items-center bg-ele-inset-strong px-5 py-4">
        <img src={ELE_LOGO_DATA_URI} alt="East London Elevators" className="h-9 w-auto" />
      </header>

      <main className="mx-auto max-w-md px-5 py-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-strong">Install the engineer app</h1>
        <p className="mt-2 text-sm text-ele-body">
          Add East London Elevators to your phone's home screen so it opens like an app and keeps working on site,
          even with no signal.
        </p>

        <section className="mt-6 rounded-ele border border-ele-green bg-ele-surface p-5 text-center">
          <p className="mb-3 font-menu text-xs uppercase tracking-widest text-[var(--ele-loler-ink)]">Scan to open the app</p>
          {qr ? (
            <img src={qr} alt="QR code for the engineer app" className="mx-auto rounded bg-white p-2" width={200} height={200} />
          ) : (
            <p className="text-sm text-ele-body">Preparing the QR code...</p>
          )}
          <p className="mt-3 break-all font-menu text-xs text-ele-green">{appUrl}</p>
        </section>

        {/* Platform-detected steps: one walkthrough on a phone, both (Android first) on desktop. */}
        <div className="mt-6 space-y-5">
          {platform === "ios" ? (
            <InstallSteps guide={IOS} />
          ) : platform === "android" ? (
            <InstallSteps guide={ANDROID} />
          ) : (
            <>
              <InstallSteps guide={ANDROID} />
              <InstallSteps guide={IOS} />
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-ele-body">
          Once installed, open the app from your home screen and log in with the credentials provided by the office.
        </p>

        {/* Session ADU: reachable in-app from the home nudge (Ask A), so give a way back to the
            jobs. A not-signed-in visitor who arrived via a shared link is sent to the sign-in page
            by the engineer guard, which is the right destination for them too. */}
        <p className="mt-6 text-center">
          <Link to="/engineer" className="font-menu text-xs tracking-wide text-ele-green">Back to your jobs</Link>
        </p>
        <p className="mt-4 text-center font-menu text-xs text-ele-body">Need help? Call the office on 0203 576 4760.</p>
      </main>
    </div>
  );
}
